/**
 * <EmptyState /> — locked structure for "nothing here yet" surfaces.
 *
 * Per DESIGN_SYSTEM.md §8 "Empty states":
 *   - Icon (60px, --text-tertiary color).
 *   - Headline: "No X yet" or "No results match your filters."
 *   - Description: 1-2 lines explaining what would appear here.
 *   - Action button: "Add X" or "Clear filters."
 *
 * Every list / table / dashboard MUST render an EmptyState; a blank
 * area is not an empty state. If there's no clear action, write
 * "Once you start using X, it will appear here." — never imply the
 * user did something wrong.
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export interface EmptyStateProps {
  /** Lucide icon component, rendered at 60px in --text-tertiary. */
  icon?: LucideIcon;
  /** Headline — short, declarative. e.g. "No employees yet". */
  headline: string;
  /** 1-2 line description. Optional but strongly encouraged. */
  description?: string;
  /** Primary action — rendered as the brand button. */
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Secondary action — rendered as a subtle text link. */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon: Icon,
  headline,
  description,
  action,
  secondaryAction,
  className = "",
}: EmptyStateProps): ReactNode {
  return (
    <div
      role="status"
      className={[
        "flex flex-col items-center justify-center text-center",
        "px-5 py-7",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {Icon && (
        <Icon
          aria-hidden="true"
          size={60}
          strokeWidth={1.25}
          className="text-text-tertiary mb-4"
        />
      )}
      <h3 className="text-lg font-semibold text-text-primary">{headline}</h3>
      {description && (
        <p className="mt-2 max-w-md text-sm text-text-secondary">
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-5 flex items-center gap-3">
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className={[
                "rounded-sm px-4 py-2 text-sm font-medium",
                "bg-brand text-brand-fg",
                "hover:bg-brand-hover",
                "transition-colors duration-micro ease-out",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-brand focus-visible:ring-offset-2",
              ].join(" ")}
            >
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className={[
                "text-sm font-medium text-text-secondary",
                "hover:text-text-primary",
                "transition-colors duration-micro ease-out",
                "focus-visible:outline-none focus-visible:underline",
              ].join(" ")}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
